import { Type } from "@sinclair/typebox";
import { isoTimestampSchema, opaqueIdSchema } from "./common.js";
export const companyVerificationTargetSchema = Type.Union([
    Type.Literal("recipient"),
    Type.Literal("billing_party")
]);
export const companyVerificationFieldSchema = Type.Union([
    Type.Literal("legal_name"),
    Type.Literal("registered_office"),
    Type.Literal("vat_number"),
    Type.Literal("tax_code"),
    Type.Literal("sdi_code")
]);
const fiscalCodeOddValues = {
    "0": 1,
    "1": 0,
    "2": 5,
    "3": 7,
    "4": 9,
    "5": 13,
    "6": 15,
    "7": 17,
    "8": 19,
    "9": 21,
    A: 1,
    B: 0,
    C: 5,
    D: 7,
    E: 9,
    F: 13,
    G: 15,
    H: 17,
    I: 19,
    J: 21,
    K: 2,
    L: 4,
    M: 18,
    N: 20,
    O: 11,
    P: 3,
    Q: 6,
    R: 8,
    S: 12,
    T: 14,
    U: 16,
    V: 10,
    W: 22,
    X: 25,
    Y: 24,
    Z: 23
};
function validVatNumber(value) {
    if (!/^\d{11}$/u.test(value) || value === "00000000000")
        return false;
    let sum = 0;
    for (let index = 0; index < 10; index += 1) {
        const digit = Number(value[index]);
        if (index % 2 === 0)
            sum += digit;
        else {
            const doubled = digit * 2;
            sum += doubled > 9 ? doubled - 9 : doubled;
        }
    }
    return (10 - (sum % 10)) % 10 === Number(value[10]);
}
function validTaxCode(value) {
    if (!/^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/u.test(value))
        return false;
    let sum = 0;
    for (let index = 0; index < 15; index += 1) {
        const character = value[index];
        if (index % 2 === 0)
            sum += fiscalCodeOddValues[character] ?? -1000;
        else
            sum +=
                character >= "0" && character <= "9" ? Number(character) : character.charCodeAt(0) - 65;
    }
    return String.fromCharCode(65 + (sum % 26)) === value[15];
}
export function normalizeItalianCompanyIdentifier(value) {
    const normalized = typeof value === "string"
        ? value
            .trim()
            .replace(/[\s.-]/gu, "")
            .toUpperCase()
        : "";
    const withoutCountry = normalized.startsWith("IT") ? normalized.slice(2) : normalized;
    if (validVatNumber(withoutCountry))
        return { value: withoutCountry, kind: "vat_number" };
    if (validTaxCode(withoutCountry))
        return { value: withoutCountry, kind: "tax_code" };
    throw Object.assign(new Error("Inserisci una partita IVA o un codice fiscale italiano formalmente valido"), { statusCode: 400 });
}
export const companyRegisteredOfficeSchema = Type.Object({
    street: Type.Optional(Type.String()),
    streetNumber: Type.Optional(Type.String()),
    postalCode: Type.Optional(Type.String()),
    town: Type.Optional(Type.String()),
    province: Type.Optional(Type.String()),
    regionCode: Type.Optional(Type.String())
}, { additionalProperties: false });
export const companyCandidateSchema = Type.Object({
    legalName: Type.String(),
    registeredOffice: Type.Optional(companyRegisteredOfficeSchema),
    vatNumber: Type.Optional(Type.String()),
    taxCode: Type.Optional(Type.String()),
    sdiCode: Type.Optional(Type.String()),
    companyStatus: Type.Optional(Type.String()),
    registrationDate: Type.Optional(Type.String()),
    observedAt: Type.Optional(isoTimestampSchema)
}, { additionalProperties: false });
export const companyVerificationProjectionSchema = Type.Object({
    id: opaqueIdSchema,
    state: Type.Union([
        Type.Literal("queued"),
        Type.Literal("running"),
        Type.Literal("completed"),
        Type.Literal("no_data"),
        Type.Literal("failed"),
        Type.Literal("blocked")
    ]),
    target: companyVerificationTargetSchema,
    identifierKind: Type.Union([Type.Literal("vat_number"), Type.Literal("tax_code")]),
    reused: Type.Boolean(),
    sourceLabel: Type.Literal("OpenAPI Company Start"),
    quality: Type.Literal("high"),
    requestedAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
    draftVersion: Type.Integer({ minimum: 1 }),
    blockReason: Type.Optional(Type.Union([
        Type.Literal("policy"),
        Type.Literal("credit"),
        Type.Literal("outcome_uncertain")
    ])),
    candidateVersion: Type.Optional(Type.Integer({ minimum: 1 })),
    candidate: Type.Optional(companyCandidateSchema),
    comparison: Type.Optional(Type.Array(Type.Object({
        field: companyVerificationFieldSchema,
        currentValue: Type.Unknown(),
        providerValue: Type.Unknown(),
        status: Type.Union([
            Type.Literal("equal"),
            Type.Literal("different"),
            Type.Literal("missing")
        ])
    }, { additionalProperties: false }))),
    lineage: Type.Optional(Type.Object({
        requestId: opaqueIdSchema,
        snapshotId: Type.Optional(opaqueIdSchema),
        factIds: Type.Array(opaqueIdSchema)
    }, { additionalProperties: false }))
}, { $id: "CompanyVerification", additionalProperties: false });
export const companyVerificationPreviewRequestSchema = Type.Object({
    identifier: Type.String({ minLength: 11, maxLength: 24 }),
    target: companyVerificationTargetSchema
}, { additionalProperties: false });
export const companyVerificationStartRequestSchema = Type.Object({
    identifier: Type.String({ minLength: 11, maxLength: 24 }),
    target: companyVerificationTargetSchema,
    previewId: opaqueIdSchema,
    approvalToken: Type.String({ minLength: 32, maxLength: 128 })
}, { additionalProperties: false });
export const companyVerificationPreviewSchema = Type.Object({
    target: companyVerificationTargetSchema,
    identifierKind: Type.Union([Type.Literal("vat_number"), Type.Literal("tax_code")]),
    normalizedIdentifier: Type.String(),
    canSubmit: Type.Boolean(),
    reusable: Type.Boolean(),
    draftVersion: Type.Integer({ minimum: 1 }),
    blockers: Type.Array(Type.String()),
    previewId: opaqueIdSchema,
    approvalToken: Type.String({ minLength: 32, maxLength: 128 })
}, { $id: "CompanyVerificationPreview", additionalProperties: false });
export const applyCompanyVerificationSchema = Type.Object({
    target: companyVerificationTargetSchema,
    draftVersion: Type.Integer({ minimum: 1 }),
    fields: Type.Array(companyVerificationFieldSchema, {
        minItems: 1,
        maxItems: 5,
        uniqueItems: true
    }),
    acknowledgeInactive: Type.Optional(Type.Boolean())
}, { additionalProperties: false });
export const workBundlePartyDraftSchema = Type.Object({
    target: companyVerificationTargetSchema,
    version: Type.Integer({ minimum: 1 }),
    value: Type.Object({
        legalName: Type.Optional(Type.String()),
        registeredOffice: Type.Optional(companyRegisteredOfficeSchema),
        vatNumber: Type.Optional(Type.String()),
        taxCode: Type.Optional(Type.String()),
        sdiCode: Type.Optional(Type.String())
    }, { additionalProperties: false }),
    fieldSources: Type.Partial(Type.Object({
        legalName: Type.Union([Type.Literal("openapi_company_start"), Type.Literal("operator")]),
        registeredOffice: Type.Union([
            Type.Literal("openapi_company_start"),
            Type.Literal("operator")
        ]),
        vatNumber: Type.Union([Type.Literal("openapi_company_start"), Type.Literal("operator")]),
        taxCode: Type.Union([Type.Literal("openapi_company_start"), Type.Literal("operator")]),
        sdiCode: Type.Union([Type.Literal("openapi_company_start"), Type.Literal("operator")])
    }, { additionalProperties: false })),
    updatedAt: isoTimestampSchema
}, { additionalProperties: false });
export const applyCompanyVerificationResponseSchema = Type.Object({
    verification: companyVerificationProjectionSchema,
    draft: workBundlePartyDraftSchema,
    applicationId: opaqueIdSchema
}, { additionalProperties: false });
//# sourceMappingURL=company-verifications.js.map